/**
 * @file FormulationSourceModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-18
 * @modified 2026-09-18
 * @description Pick the product whose formulation another product should
 *              report.
 *
 *              A modal rather than a dropdown. The dropdown this replaces sat
 *              inside a table cell and a scrolling panel, so its menu was
 *              clipped by its own container — the list could not overlap out of
 *              the box it lived in, which made choosing from three hundred
 *              products a fight. A modal has the whole screen and a search box
 *              that is actually usable.
 */

import { useState, useMemo, useEffect } from 'react';
import Modal from './Modal';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import './TableStyles.css';
import './FormulationSourceModal.css';

function FormulationSourceModal({ isOpen, forItem, items = [], currentSourceId = null, loading = false, onPick, onClose }) {
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState(currentSourceId);

  useEffect(() => { if (isOpen) { setSearch(''); setPicked(currentSourceId); } }, [isOpen, currentSourceId]);
  useSlideOutButton([isOpen, picked]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;
    return items.filter((i) => `${i.item_code} ${i.item_name}`.toLowerCase().includes(term));
  }, [items, search]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Use another product's formulation"
      className="formulation-source"
      size="lg"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={() => picked && onPick(picked)} disabled={!picked}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">check</span></div>
              <span className="btn-label">Use this formulation</span>
            </button>
          </div>
        </div>
      }
    >
      {forItem && (
        <p className="fsm-for">
          <span className="account-badge-inline">{forItem.item_code}</span>
          <span className="fsm-for-name">{forItem.item_name}</span>
          <span className="fsm-for-note">
            will report the analysis of whichever product you choose, until it has a formulation of its own.
          </span>
        </p>
      )}

      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by code or name…"
        autoFocus
      />

      {loading ? <LoadingSpinner /> : (
        <div className="table-container fsm-list">
          <table className="data-table">
            <thead>
              <tr>
                <th className="fsm-pick-col" />
                <th>Product</th>
                <th>Sales code</th>
                <th className="num">Versions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => (
                <tr
                  key={i.id}
                  className={`line-row${picked === i.id ? ' selected' : ''}`}
                  onClick={() => setPicked(i.id)}
                  onDoubleClick={() => onPick(i.id)}
                >
                  <td className="fsm-pick-col">
                    <span className="material-symbols-rounded">
                      {picked === i.id ? 'radio_button_checked' : 'radio_button_unchecked'}
                    </span>
                  </td>
                  <td>
                    <span className="account-badge-inline">{i.item_code}</span>
                    <span className="fsm-name">{i.item_name}</span>
                  </td>
                  <td>{i.sales_code || '—'}</td>
                  <td className="num">{i.iterations}</td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={4} className="report-empty">No product matches.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

export default FormulationSourceModal;
